import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Inventory } from "../../../../../api";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { ACTIONS } from "../../../../../utils/constants";

import styles from "./InventoryExitPage.module.scss";
import {
  getInitialValues,
  getTodayDate,
  createExitItem,
  flattenProducts,
  formatCurrency,
  formatNumber,
  validationSchema,
} from "./InventoryExitPage.form";

function getExistencesResponse(result) {
  const response =
    result?.existences ||
    result?.data?.existences ||
    result?.response?.data?.existences ||
    {};

  if (Array.isArray(response?.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return {
      docs: response,
      totalDocs: response.length,
    };
  }

  return {
    docs: [],
    totalDocs: 0,
  };
}

function getErrorMessage(error) {
  if (!error) {
    return "No fue posible completar la operación.";
  }

  if (typeof error === "string") {
    return error;
  }

  return error.msg || error.message || "No fue posible completar la operación.";
}

function getItemError(errors, index, fieldName) {
  const itemErrors = errors?.items;

  if (!Array.isArray(itemErrors)) {
    return "";
  }

  const rowError = itemErrors[index];

  if (typeof rowError === "string") {
    return rowError;
  }

  return rowError?.[fieldName] || "";
}

function LocationVariantAutoAdder({
  selectedVariant,
  items,
  setFieldValue,
}) {
  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    const alreadyAdded = items.some(
      (item) =>
        String(item.product) === String(selectedVariant.productId) &&
        String(item.variantId) === String(selectedVariant.variantId)
    );

    if (!alreadyAdded) {
      setFieldValue("items", [
        ...items,
        createExitItem(selectedVariant),
      ]);
    }
  }, [items, selectedVariant, setFieldValue]);

  return null;
}

export function InventoryExitPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { hasAction } = usePermissions();
  // Salida de inventario es un permiso propio y específico (EXIT), no el
  // genérico "ADD" -- el botón de la lista ya se oculta sin este permiso,
  // pero esta página se puede alcanzar por URL directa, así que el
  // envío del formulario también se bloquea acá.
  const canExit = hasAction("inventory", ACTIONS.EXIT);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [availableVariants, setAvailableVariants] = useState([]);

  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [serverError, setServerError] = useState("");

  const initialSelectedProduct = location.state?.productId || "";
  const initialSelectedVariant = location.state?.variantId || "";

  const loadVariants = useCallback(async () => {
    try {
      setIsLoadingVariants(true);
      setServerError("");

      const inventoryController = new Inventory();

      const result = await inventoryController.getExistences(
        1,
        100,
        {
          search: appliedSearch || undefined,
        }
      );

      const response = getExistencesResponse(result);

      setAvailableVariants(flattenProducts(response.docs || []));
    } catch (errorResponse) {
      console.error("load exit variants error:", errorResponse);

      setAvailableVariants([]);

      setServerError(
        getErrorMessage(errorResponse) ||
          "No fue posible cargar productos y variantes."
      );
    } finally {
      setIsLoadingVariants(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const selectedLocationVariant = useMemo(() => {
    if (!initialSelectedProduct || !initialSelectedVariant) {
      return null;
    }

    return availableVariants.find(
      (variant) =>
        String(variant.productId) === String(initialSelectedProduct) &&
        String(variant.variantId) === String(initialSelectedVariant)
    );
  }, [
    availableVariants,
    initialSelectedProduct,
    initialSelectedVariant,
  ]);

  function handleSearchSubmit() {
    setAppliedSearch(searchValue.trim());
  }

  function handleSearchInputKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  }

  function handleCancel() {
    navigate("/admin/inventory");
  }

  async function handleSaveDraft(values) {
    try {
      setIsSavingDraft(true);

      window.localStorage.setItem(
        "inventory-exit-draft",
        JSON.stringify({
          reference: values.reference,
          notes: values.notes,
          saleDate: values.saleDate,
          items: values.items,
          savedAt: new Date().toISOString(),
        })
      );

      setServerError("");
    } catch (error) {
      console.error("save inventory exit draft error:", error);

      setServerError(
        "No fue posible guardar el borrador localmente."
      );
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleSubmit(values, { setSubmitting, setErrors }) {
    try {
      setServerError("");

      const payload = {
        reference: String(values.reference || "").trim(),
        notes: String(values.notes || "").trim(),
        saleDate: String(values.saleDate || "").trim(),
        items: values.items.map((item) => ({
          product: String(item.product || "").trim(),
          variantId: String(item.variantId || "").trim(),
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
        })),
      };

      const inventoryController = new Inventory();

      await inventoryController.createExit(payload);

      navigate("/admin/inventory", {
        state: {
          success: "Salida de inventario registrada correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("createExit error:", errorResponse);

      const responseData =
        errorResponse?.response?.data ||
        errorResponse?.data ||
        errorResponse ||
        {};

      if (responseData?.errors) {
        setErrors(responseData.errors);
      }

      setServerError(
        responseData?.msg ||
          responseData?.message ||
          "No fue posible registrar la salida de inventario."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className={styles.InventoryExitPage}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          setFieldValue,
          isSubmitting,
        }) => {
          const totalItems = values.items.length;

          const totalQuantity = values.items.reduce(
            (total, item) => total + (Number(item.quantity) || 0),
            0
          );

          const totalEstimatedProfit = values.items.reduce(
            (total, item) =>
              total +
              ((Number(item.unitPrice) || 0) - (Number(item.cost) || 0)) *
                (Number(item.quantity) || 0),
            0
          );

          function handleAddVariant(variant) {
            const alreadyAdded = values.items.some(
              (item) =>
                String(item.product) === String(variant.productId) &&
                String(item.variantId) === String(variant.variantId)
            );

            if (alreadyAdded) {
              setServerError(
                "Esta variante ya fue agregada al detalle de la salida."
              );

              return;
            }

            if (Number(variant.availableStock) <= 0) {
              setServerError(
                "No puedes agregar una variante sin existencias disponibles."
              );

              return;
            }

            setFieldValue("items", [
              ...values.items,
              createExitItem(variant),
            ]);

            setServerError("");
          }

          function handleRemoveItem(localId) {
            setFieldValue(
              "items",
              values.items.filter((item) => item.localId !== localId)
            );
          }

          return (
            <>
              <LocationVariantAutoAdder
                selectedVariant={selectedLocationVariant}
                items={values.items}
                setFieldValue={setFieldValue}
              />

              <header className={styles.PageHeader}>
                <div className={styles.TitleSection}>
                  <Link
                    to="/admin/inventory"
                    className={styles.BackButton}
                    aria-label="Volver a existencias"
                  >
                    ←
                  </Link>

                  <div>
                    <p className={styles.Breadcrumb}>
                      Inventario / Salida de inventario
                    </p>

                    <h1>Salida de inventario</h1>

                    <p className={styles.Subtitle}>
                      Registra unidades retiradas y actualiza las existencias
                      disponibles de cada variante.
                    </p>
                  </div>
                </div>

                <div className={styles.HeaderActions}>
                  <button
                    type="button"
                    className={styles.CancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={styles.DraftButton}
                    onClick={() => handleSaveDraft(values)}
                    disabled={isSubmitting || isSavingDraft}
                  >
                    {isSavingDraft
                      ? "Guardando..."
                      : "Guardar borrador"}
                  </button>

                  <button
                    type="submit"
                    form="inventory-exit-form"
                    className={styles.ConfirmButton}
                    disabled={isSubmitting || !canExit}
                  >
                    {isSubmitting
                      ? "Confirmando..."
                      : "Confirmar salida"}
                  </button>
                </div>
              </header>

              {!canExit && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>
                    No tienes permiso para registrar salidas de inventario.
                  </span>
                </div>
              )}

              {serverError && (
                <div className={styles.ServerErrorMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>{serverError}</span>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <div>
                    <strong>Revisa los datos de la salida.</strong>

                    <span>
                      Corrige los campos marcados antes de confirmar el
                      movimiento.
                    </span>
                  </div>
                </div>
              )}

              <Form
                id="inventory-exit-form"
                className={styles.FormContent}
              >
                <div className={styles.FormColumns}>
                  <section className={styles.GeneralInformationCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Información general</h2>

                        <p>
                          Registra una referencia y las notas del egreso.
                        </p>
                      </div>
                    </header>

                    <div className={styles.GeneralInformationContent}>
                      <div className={styles.FormField}>
                        <label htmlFor="saleDate">Fecha de venta</label>

                        <Field
                          id="saleDate"
                          name="saleDate"
                          type="date"
                          max={getTodayDate()}
                          disabled={isSubmitting}
                        />

                        <span className={styles.FieldHelp}>
                          Define a qué mes corresponde la venta. Cámbiala si
                          registras una venta de un día anterior.
                        </span>

                        <ErrorMessage
                          name="saleDate"
                          component="span"
                          className={styles.FieldError}
                        />
                      </div>

                      <div className={styles.FormField}>
                        <label htmlFor="reference">
                          Referencia / Documento
                        </label>

                        <Field
                          id="reference"
                          name="reference"
                          type="text"
                          placeholder="Ej. SAL-2025-001254"
                          disabled={isSubmitting}
                        />

                        <ErrorMessage
                          name="reference"
                          component="span"
                          className={styles.FieldError}
                        />
                      </div>

                      <div className={styles.FormField}>
                        <label htmlFor="notes">Notas</label>

                        <Field
                          as="textarea"
                          id="notes"
                          name="notes"
                          placeholder="Ej. Salida por venta, devolución a proveedor o uso interno."
                          maxLength="1000"
                          disabled={isSubmitting}
                        />

                        <div className={styles.TextAreaFooter}>
                          <ErrorMessage
                            name="notes"
                            component="span"
                            className={styles.FieldError}
                          />

                          <span>{values.notes.length} / 1000</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className={styles.ProductSearchCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Buscar producto / variante</h2>

                        <p>
                          Busca una variante con existencias disponibles.
                        </p>
                      </div>
                    </header>

                    <div className={styles.ProductSearchContent}>
                      <div className={styles.SearchForm}>
                        <input
                          type="search"
                          value={searchValue}
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                          onKeyDown={handleSearchInputKeyDown}
                          placeholder="Buscar producto, SKU o código de barras..."
                          aria-label="Buscar producto o variante"
                          disabled={isLoadingVariants || isSubmitting}
                        />

                        <button
                          type="button"
                          onClick={handleSearchSubmit}
                          aria-label="Buscar"
                          disabled={isLoadingVariants || isSubmitting}
                        >
                          ⌕
                        </button>
                      </div>

                      {isLoadingVariants ? (
                        <div className={styles.SearchState}>
                          Cargando variantes...
                        </div>
                      ) : availableVariants.length === 0 ? (
                        <div className={styles.SearchState}>
                          No se encontraron productos o variantes.
                        </div>
                      ) : (
                        <div className={styles.SearchResults}>
                          {availableVariants.map((variant) => {
                            const alreadyAdded = values.items.some(
                              (item) =>
                                String(item.product) ===
                                  String(variant.productId) &&
                                String(item.variantId) ===
                                  String(variant.variantId)
                            );

                            const hasStock =
                              Number(variant.availableStock) > 0;

                            return (
                              <article
                                key={`${variant.productId}-${variant.variantId}`}
                                className={styles.ProductResult}
                              >
                                {variant.productImage ? (
                                  <img
                                    src={variant.productImage}
                                    alt={variant.productName}
                                  />
                                ) : (
                                  <span
                                    className={
                                      styles.ProductImagePlaceholder
                                    }
                                    aria-hidden="true"
                                  >
                                    ▣
                                  </span>
                                )}

                                <div className={styles.ProductResultInfo}>
                                  <strong title={variant.productName}>
                                    {variant.productName}
                                  </strong>

                                  <span
                                    className={styles.ResultSku}
                                    title={`SKU: ${variant.sku || "Sin SKU"}`}
                                  >
                                    SKU: {variant.sku || "Sin SKU"}
                                  </span>

                                  <span
                                    className={styles.ResultVariant}
                                    title={variant.variantLabel}
                                  >
                                    {variant.variantLabel}
                                  </span>

                                  <span
                                    className={
                                      hasStock
                                        ? styles.ResultAvailable
                                        : styles.ResultOutOfStock
                                    }
                                  >
                                    Disponible:{" "}
                                    {formatNumber(
                                      variant.availableStock
                                    )}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className={styles.AddProductButton}
                                  onClick={() => handleAddVariant(variant)}
                                  disabled={
                                    isSubmitting ||
                                    alreadyAdded ||
                                    !hasStock
                                  }
                                >
                                  {!hasStock
                                    ? "Sin stock"
                                    : alreadyAdded
                                    ? "Agregado"
                                    : "Agregar"}
                                </button>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <section className={styles.DetailCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Detalle de productos</h2>

                      <p>
                        La cantidad retirada no puede superar las existencias
                        disponibles.
                      </p>
                    </div>

                    <span className={styles.ItemsCounter}>
                      {totalItems}{" "}
                      {totalItems === 1 ? "producto" : "productos"}
                    </span>
                  </header>

                  {typeof errors.items === "string" && (
                    <div className={styles.DetailValidationMessage}>
                      {errors.items}
                    </div>
                  )}

                  {values.items.length === 0 ? (
                    <div className={styles.EmptyDetailState}>
                      <p>
                        Busca y agrega una variante para registrar la salida.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.TableContainer}>
                        <table className={styles.Table}>
                          <thead>
                            <tr>
                              <th>Producto</th>
                              <th>SKU</th>
                              <th>Variante</th>
                              <th>Stock</th>
                              <th>Disponible</th>
                              <th>Cantidad a retirar</th>
                              <th>Precio de venta</th>
                              <th aria-label="Acciones" />
                            </tr>
                          </thead>

                          <tbody>
                            {values.items.map((item, index) => {
                              const quantityError = getItemError(
                                errors,
                                index,
                                "quantity"
                              );

                              const unitPriceError = getItemError(
                                errors,
                                index,
                                "unitPrice"
                              );

                              return (
                                <tr key={item.localId}>
                                  <td>
                                    <div className={styles.ProductCell}>
                                      {item.productImage ? (
                                        <img
                                          src={item.productImage}
                                          alt={item.productName}
                                        />
                                      ) : (
                                        <span
                                          className={
                                            styles.ProductImagePlaceholder
                                          }
                                          aria-hidden="true"
                                        >
                                          ▣
                                        </span>
                                      )}

                                      <div>
                                        <strong title={item.productName}>
                                          {item.productName}
                                        </strong>

                                        <span
                                          title={
                                            item.productCode || "Sin código"
                                          }
                                        >
                                          {item.productCode ||
                                            "Sin código"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.SkuCode}
                                      title={item.sku || "—"}
                                    >
                                      {item.sku || "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.VariantLabel}
                                      title={item.variantLabel}
                                    >
                                      {item.variantLabel}
                                    </span>
                                  </td>

                                  <td>
                                    <span className={styles.StockValue}>
                                      {formatNumber(item.stock)}
                                    </span>
                                  </td>

                                  <td>
                                    <span className={styles.AvailableStock}>
                                      {formatNumber(item.availableStock)}
                                    </span>
                                  </td>

                                  <td>
                                    <Field
                                      name={`items[${index}].quantity`}
                                      type="number"
                                      min="1"
                                      max={item.availableStock}
                                      step="1"
                                      className={
                                        quantityError
                                          ? styles.error
                                          : ""
                                      }
                                      disabled={isSubmitting}
                                    />

                                    {quantityError && (
                                      <span className={styles.FieldError}>
                                        {quantityError}
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <Field
                                      name={`items[${index}].unitPrice`}
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      className={
                                        unitPriceError
                                          ? styles.error
                                          : ""
                                      }
                                      disabled={isSubmitting}
                                    />

                                    {unitPriceError && (
                                      <span className={styles.FieldError}>
                                        {unitPriceError}
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <button
                                      type="button"
                                      className={styles.RemoveItemButton}
                                      onClick={() =>
                                        handleRemoveItem(item.localId)
                                      }
                                      disabled={isSubmitting}
                                      aria-label={`Eliminar ${item.productName}`}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <footer className={styles.DetailSummary}>
                        <div>
                          <span>Total ítems</span>
                          <strong>{totalItems}</strong>
                        </div>

                        <div>
                          <span>Total unidades a retirar</span>
                          <strong>{formatNumber(totalQuantity)}</strong>
                        </div>

                        <div>
                          <span>Ganancia estimada</span>
                          <strong>
                            {formatCurrency(totalEstimatedProfit)}
                          </strong>
                        </div>

                        <div className={styles.WarningSummary}>
                          <span>Validación</span>
                          <strong>
                            No se puede retirar más del disponible
                          </strong>
                        </div>
                      </footer>
                    </>
                  )}
                </section>
              </Form>
            </>
          );
        }}
      </Formik>
    </section>
  );
}